import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Input,
  Textarea,
  useToast,
  HStack,
  Box,
  Badge,
  useColorModeValue
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { processDonation } from '../services/paymentService';
import { useNavigate } from 'react-router-dom';

interface DonationPaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  contentTitle: string;
  creatorId: string;
  creatorName: string;
  onDonationComplete?: () => void;
}

const DonationPaywallModal: React.FC<DonationPaywallModalProps> = ({
  isOpen,
  onClose,
  contentId,
  contentTitle,
  creatorId,
  creatorName,
  onDonationComplete
}) => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('5.00');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');

  const handleDonate = async () => {
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to make a donation.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      navigate('/login');
      return;
    }

    const donationAmount = parseFloat(amount);
    if (isNaN(donationAmount) || donationAmount < 1) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a minimum donation of $1.00',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setIsProcessing(true);
    try {
      await processDonation(
        user.uid,
        creatorId,
        donationAmount,
        message.trim() || undefined,
        contentId,
        userProfile?.name || user.displayName || 'Anonymous'
      );

      toast({
        title: 'Donation successful!',
        description: 'Thank you for supporting this creator. You now have access to this content.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });

      onDonationComplete?.();
      onClose();
    } catch (error: any) {
      console.error('Donation error:', error);
      toast({
        title: 'Donation failed',
        description: error.message || 'Unable to process donation. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const quickAmounts = ['5.00', '10.00', '25.00', '50.00'];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>
          <VStack align="start" gap={2}>
            <Text fontSize="xl" fontWeight="bold">
              Donation Required
            </Text>
            <Text fontSize="sm" color="gray.500" fontWeight="normal">
              Support {creatorName} to access this content
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack gap={4} align="stretch">
            <Box p={4} bg="purple.50" _dark={{ bg: 'purple.900', borderColor: 'purple.700' }} borderRadius="md" border="1px" borderColor="purple.200">
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Content: <Badge colorScheme="purple">{contentTitle}</Badge>
              </Text>
              <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                This creator requires a donation to view this content. Your donation directly supports the creator and grants you access.
              </Text>
            </Box>

            <VStack gap={3} align="stretch">
              <Text fontWeight="medium">Donation Amount</Text>
              
              <HStack gap={2} flexWrap="wrap">
                {quickAmounts.map((quickAmount) => (
                  <Button
                    key={quickAmount}
                    size="sm"
                    variant={amount === quickAmount ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => setAmount(quickAmount)}
                  >
                    ${quickAmount}
                  </Button>
                ))}
              </HStack>

              <Input
                type="number"
                placeholder="Enter custom amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="1"
                step="0.01"
                bg={useColorModeValue('gray.50', 'gray.700')}
              />
            </VStack>

            <VStack gap={2} align="stretch">
              <Text fontWeight="medium">Message (Optional)</Text>
              <Textarea
                placeholder="Leave a message for the creator..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                bg={useColorModeValue('gray.50', 'gray.700')}
                maxLength={500}
              />
              <Text fontSize="xs" color="gray.500" textAlign="right">
                {message.length}/500
              </Text>
            </VStack>

            {!user && (
              <Box p={3} bg="yellow.50" _dark={{ bg: 'yellow.900', borderColor: 'yellow.700' }} borderRadius="md" border="1px" borderColor="yellow.200">
                <Text fontSize="sm" color="yellow.800" _dark={{ color: 'yellow.200' }}>
                  You need to sign in to make a donation.
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack gap={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleDonate}
              isLoading={isProcessing}
              loadingText="Processing..."
              isDisabled={!user}
            >
              Donate & Access Content
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DonationPaywallModal;

